// D41: full-time screen — final score + Rematch / New game. Rematch is room-scoped: every player who
// presses it lands in the SAME room's next match (server starts it at a ≥2/3 tally, T-SRV-012).
// ui/ touches only the DOM with injected data + callbacks (T-ARCH-004).

export interface EndScreenOpts {
  teamColors: [number, number];
  names?: [string, string];
  /** Called when the player presses Rematch. Return false to keep the button enabled (local restart). */
  onRematch: () => void;
  onNewGame: () => void;
}

export class EndScreen {
  private readonly el: HTMLDivElement;
  private readonly scoreEl: HTMLDivElement;
  private readonly voteEl: HTMLDivElement;
  private readonly rematchBtn: HTMLButtonElement;
  private shown = false;

  constructor(doc: Document, opts: EndScreenOpts) {
    const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;
    const names = opts.names ?? ['BLUE', 'RED'];
    this.el = doc.createElement('div');
    this.el.style.cssText =
      'position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(3,14,8,.82);z-index:110;' +
      'font-family:ui-monospace,monospace;color:#eaffea';
    const panel = doc.createElement('div');
    panel.style.cssText =
      'padding:28px 42px;border:1px solid #2a5a3a;border-radius:16px;background:#08160e;text-align:center;box-shadow:0 8px 40px rgba(0,0,0,.5)';
    const title = doc.createElement('div');
    title.style.cssText = 'font-size:15px;letter-spacing:4px;opacity:.75;font-weight:700';
    title.textContent = '⏱ FULL-TIME';
    this.scoreEl = doc.createElement('div');
    this.scoreEl.style.cssText = 'font-size:38px;font-weight:800;margin:14px 0 4px';
    const teams = doc.createElement('div');
    teams.style.cssText = 'font-size:13px;font-weight:700;letter-spacing:2px;margin-bottom:18px';
    const teamSpan = (name: string, color: string): HTMLSpanElement => {
      const sp = doc.createElement('span');
      sp.style.color = color;
      sp.textContent = name;
      return sp;
    };
    const dot = doc.createElement('span');
    dot.textContent = ' · ';
    teams.append(teamSpan(names[0], hex(opts.teamColors[0])), dot, teamSpan(names[1], hex(opts.teamColors[1])));
    const mkBtn = (label: string, primary: boolean, onClick: () => void): HTMLButtonElement => {
      const b = doc.createElement('button');
      b.textContent = label;
      b.style.cssText =
        `margin:4px 8px;padding:10px 24px;font:700 16px ui-monospace,monospace;border-radius:9px;cursor:pointer;` +
        (primary ? 'border:1px solid #6f6;background:#1a5c30;color:#eaffea' : 'border:1px solid #476;background:#0d2418;color:#cde');
      b.onclick = onClick;
      return b;
    };
    this.rematchBtn = mkBtn('🔄 Rematch', true, () => {
      opts.onRematch();
      this.rematchBtn.disabled = true;
      this.rematchBtn.style.opacity = '0.6';
      this.rematchBtn.textContent = '✓ Ready — waiting…';
    });
    const newBtn = mkBtn('🏠 New game', false, opts.onNewGame);
    this.voteEl = doc.createElement('div');
    this.voteEl.style.cssText = 'margin-top:12px;font-size:12px;opacity:.8;min-height:15px';
    panel.append(title, this.scoreEl, teams, this.rematchBtn, newBtn, this.voteEl);
    this.el.appendChild(panel);
    doc.body.appendChild(this.el);
  }

  show(score: [number, number]): void {
    this.scoreEl.textContent = `${score[0]} : ${score[1]}`;
    this.el.style.display = 'flex';
    this.shown = true;
  }

  hide(): void {
    if (!this.shown) return;
    this.el.style.display = 'none';
    this.shown = false;
    this.rematchBtn.disabled = false;
    this.rematchBtn.style.opacity = '1';
    this.rematchBtn.textContent = '🔄 Rematch';
    this.voteEl.textContent = '';
  }

  get visible(): boolean {
    return this.shown;
  }

  /** Rematch tally from the server (REMATCH_STATE broadcast). */
  setVotes(votes: number, needed: number): void {
    this.voteEl.textContent = `rematch votes: ${votes} / ${needed}`;
  }
}
